import { type, type Traversal, type Type } from 'arktype';
import { dateKey } from '@zag/core/models';
import { badgeLookupAssignmentSchema, badgeLookupScanSchema, type BadgeLookupAssignment, type BadgeLookupScan } from './badge-lookup.model';
import { projectTmcXBadge } from './badge-projection';
import { personBadgeAssignmentSchema, type PersonBadgeAssignment } from './terminals-api.model';

export const BADGE_CARD_TYPES = ['mifare', 'legic', 'hid', 'other'] as const;
export type BadgeCardType = (typeof BADGE_CARD_TYPES)[number];

export const BADGE_CONDITIONS = ['in_service', 'lost', 'damaged', 'retired'] as const;
export type BadgeCondition = (typeof BADGE_CONDITIONS)[number];

export const BADGE_STOCK_STATUSES = ['available', 'assigned', 'lost', 'damaged', 'retired'] as const;
export type BadgeStockStatus = (typeof BADGE_STOCK_STATUSES)[number];

export const BADGE_RANGE_MAX = 500;

const uuidString = type('string.uuid').configure({ description: 'UUID.' });
const cardTypeSchema: Type<BadgeCardType> = type.enumerated(...BADGE_CARD_TYPES).configure({ description: 'Physical card technology.' });
const conditionSchema: Type<BadgeCondition> = type
  .enumerated(...BADGE_CONDITIONS)
  .configure({ description: 'Stored card condition; availability is derived.' });
const statusSchema: Type<BadgeStockStatus> = type
  .enumerated(...BADGE_STOCK_STATUSES)
  .configure({ description: 'Derived status: assigned = current or upcoming assignment; available = in service and unassigned.' });
const badgeInput = type('1 <= string <= 64').configure({ description: 'Badge number as read by the terminal.' });
const printedNumberInput = type('1 <= string <= 64')
  .or(type('null'))
  .configure({ description: 'Number printed on the card, unique when set.' });
const nullableCardType = cardTypeSchema.or(type('null'));
const receivedOnInput = dateKey.configure({ description: 'Day the card was received; defaults to today.' });

export function expandBadgeRange(from: string, to: string): string[] {
  const start = Number(from);
  return Array.from({ length: Number(to) - start + 1 }, (_, index) => String(start + index).padStart(from.length, '0'));
}

const rangeBound = type(/^\d+$/)
  .narrow((value, ctx) => projectTmcXBadge(value) !== null || ctx.mustBe('up to 10 significant digits'))
  .configure({ description: 'Badge number of digits only, up to 10 significant digits.' });

function withinRangeLimit({ rangeFrom, rangeTo }: { rangeFrom: string; rangeTo: string }, ctx: Traversal): boolean {
  const count = Number(rangeTo) - Number(rangeFrom) + 1;
  if (count < 1) return ctx.reject({ path: ['rangeTo'], expected: 'not before rangeFrom' });
  return count <= BADGE_RANGE_MAX || ctx.reject({ path: ['rangeTo'], expected: `at most ${BADGE_RANGE_MAX} cards after rangeFrom` });
}

export type CreateBadgeStock =
  | { badge: string; printedNumber?: string | null; cardType?: BadgeCardType | null; receivedOn?: string }
  | { rangeFrom: string; rangeTo: string; cardType?: BadgeCardType | null; receivedOn?: string };

const createSingleBadgeStockSchema = type({
  '+': 'reject',
  badge: badgeInput,
  'printedNumber?': printedNumberInput,
  'cardType?': nullableCardType,
  'receivedOn?': receivedOnInput,
});

const createBadgeRangeStockSchema = type({
  '+': 'reject',
  rangeFrom: rangeBound,
  rangeTo: rangeBound,
  'cardType?': nullableCardType,
  'receivedOn?': receivedOnInput,
}).narrow(withinRangeLimit);

export const createBadgeStockSchema = createSingleBadgeStockSchema
  .or(createBadgeRangeStockSchema)
  .configure({ description: `Add one card, or a number range of at most ${BADGE_RANGE_MAX} cards keeping the digit width of rangeFrom.` });

export interface UpdateBadgeStock {
  version: number;
  printedNumber?: string | null;
  cardType?: BadgeCardType | null;
  condition?: BadgeCondition;
}

export const updateBadgeStockSchema = type({
  version: type('number.integer >= 1').configure({ description: 'Version the change is based on.' }),
  'printedNumber?': printedNumberInput,
  'cardType?': nullableCardType,
  'condition?': conditionSchema,
}).configure({ description: 'Partial card update.' });

export interface SwapPersonBadge {
  newBadge: string;
  oldCondition: 'lost' | 'damaged';
  date: string;
}

export const swapPersonBadgeSchema: Type<SwapPersonBadge> = type({
  newBadge: badgeInput,
  oldCondition: type("'lost' | 'damaged'").configure({ description: 'Condition the replaced card gets.' }),
  date: dateKey.configure({ description: 'First day of the new card; the old assignment ends the day before.' }),
}).configure({ description: 'Replace the card of a current assignment.' });

export interface BadgeStockListQuery {
  status?: BadgeStockStatus;
  search?: string;
  limit?: number;
  offset?: number;
}

export const badgeStockListQuerySchema = type({
  'status?': statusSchema,
  'search?': 'string',
  'limit?': type('string.integer.parse').to('1 <= number <= 200'),
  'offset?': type('string.integer.parse').to('number >= 0'),
});

export interface BadgeStockAvailableQuery {
  search?: string;
  limit?: number;
}

export const badgeStockAvailableQuerySchema = type({
  'search?': 'string',
  'limit?': type('string.integer.parse').to('1 <= number <= 100'),
});

export interface BadgeStockHolder {
  personId: string;
  firstName: string;
  lastName: string;
}

export interface BadgeStockItem {
  id: string;
  badge: string;
  projectedBadge: string;
  printedNumber: string | null;
  cardType: BadgeCardType | null;
  receivedOn: string;
  condition: BadgeCondition;
  status: BadgeStockStatus;
  holder: BadgeStockHolder | null;
  version: number;
}

export const badgeStockItemSchema: Type<BadgeStockItem> = type({
  id: uuidString,
  badge: type('string').configure({ description: 'Badge number as entered.' }),
  projectedBadge: type('string').configure({ description: 'Normalized 10-digit reader number.' }),
  printedNumber: type('string | null').configure({ description: 'Number printed on the card, or null.' }),
  cardType: nullableCardType,
  receivedOn: dateKey,
  condition: conditionSchema,
  status: statusSchema,
  holder: type({ personId: uuidString, firstName: 'string', lastName: 'string' })
    .or(type('null'))
    .configure({ description: 'Current holder, or null when none or outside your person scope.' }),
  version: 'number.integer >= 1',
}).configure({ description: 'One card of the badge stock.' });

export type BadgeStockCounts = Record<BadgeStockStatus, number>;

export interface BadgeStockListResponse {
  counts: BadgeStockCounts;
  items: BadgeStockItem[];
  total: number;
}

const countSchema = type('number.integer >= 0');

export const badgeStockListResponseSchema: Type<BadgeStockListResponse> = type({
  counts: type({ available: countSchema, assigned: countSchema, lost: countSchema, damaged: countSchema, retired: countSchema }).configure({
    description: 'Cards per status over the whole stock, unfiltered.',
  }),
  items: badgeStockItemSchema.array(),
  total: countSchema.configure({ description: 'Cards matching the filter.' }),
}).configure({ description: 'Stock counts and one page of cards ordered by reader number.' });

export interface CreateBadgeStockResponse {
  created: BadgeStockItem[];
  skipped: string[];
}

export const createBadgeStockResponseSchema: Type<CreateBadgeStockResponse> = type({
  created: badgeStockItemSchema.array(),
  skipped: type('string[]').configure({ description: 'Entered numbers already in stock.' }),
}).configure({ description: 'Cards added to the stock.' });

export type BadgeStockDetail = BadgeStockItem & {
  assignments: BadgeLookupAssignment[];
  scans: BadgeLookupScan[];
  unassignedScans: BadgeLookupScan[] | null;
};

export const badgeStockDetailSchema: Type<BadgeStockDetail> = badgeStockItemSchema
  .and({
    assignments: badgeLookupAssignmentSchema.array(),
    scans: badgeLookupScanSchema.array(),
    unassignedScans: badgeLookupScanSchema
      .array()
      .or(type('null'))
      .configure({ description: 'Scans of the badge without an assignment; null without terminals:evidence:read.' }),
  })
  .configure({ description: 'One card with its assignment history and recent scans.' });

export interface SwapPersonBadgeResponse {
  ended: PersonBadgeAssignment;
  created: PersonBadgeAssignment;
}

export const swapPersonBadgeResponseSchema: Type<SwapPersonBadgeResponse> = type({
  ended: personBadgeAssignmentSchema,
  created: personBadgeAssignmentSchema,
}).configure({ description: 'The ended old assignment and the new one.' });
